import { ReactNode, useMemo, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import {
  Button,
  Divider,
  Dropdown,
  Group,
  IconCaretDown,
  IconGear,
  IconMinusCircle,
  IconThreeDotsVer,
  Typography,
  useDropdownState,
  Wrapper
} from '@screentone/core';
import classnames from 'classnames';

import { UiModuleFieldsHeader } from 'components/datamodel/container/Module/components/ui-module-fields-header/UiModuleFieldsHeader';
import { useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import { PageModule, UiModuleType } from 'data/generated/graphql';
import { FlatSlot, swapAdjacentItems } from 'utils/layoutUtils';
import { findTreatmentSetting, getModuleFieldKey, getSubsetTreatmentTypeSettings } from 'utils/modules';
import styles from './ModuleItem.module.scss';
import { ReorderControls } from './ReorderControls';
import { EditModuleModal } from '../modals/EditModuleModal';

interface ModuleItemProps {
  module: FlatSlot;
  moduleIndex: number;
  localIndex: number;
  isFirst: boolean;
  isLast: boolean;
  groupAllowedTreatmentTypes: string[];
  /** Extra content rendered after the module box (e.g. zone droppables for the groups page editor). */
  extraContent?: ReactNode;
  /** When provided, "Module Options" calls this instead of opening the internal EditModuleModal. */
  onEditModule?: () => void;
  /** When provided, treatment-type buttons become active and call this on click. */
  onChangeTreatmentType?: (treatmentType: string) => void;
}

export const ModuleItem = ({
  module,
  moduleIndex,
  localIndex,
  isFirst,
  isLast,
  groupAllowedTreatmentTypes,
  extraContent,
  onEditModule,
  onChangeTreatmentType
}: ModuleItemProps) => {
  const { open, setOpen, componentRef } = useDropdownState();
  const { open: treatmentOpen, setOpen: setTreatmentOpen, componentRef: treatmentComponentRef } = useDropdownState();
  const { modules, updateModule, reorderModules, removeModule, treatmentTypeSettings } = useStructureEditContext();

  const currentTreatmentType = module.ttKey;
  const uiModuleType = findTreatmentSetting(treatmentTypeSettings, currentTreatmentType)?.module?.uiModuleType ?? null;
  const uiModuleFields = module.uiModuleFields ?? {};
  const maxItemsOverride = module.maxItemsOverride ?? null;
  const minItemsOverride = module.minItemsOverride ?? null;

  const asPageModule = useMemo(
    (): PageModule => ({
      moduleId: null,
      uiModuleType: uiModuleType ?? UiModuleType.UiBasicModuleType,
      uiModuleFields: uiModuleFields as PageModule['uiModuleFields'],
      moduleItems: [],
      maxItemsOverride,
      minItemsOverride,
      ignoreBaseQuery: module.ignoreBaseQuery ?? null,
      optional: module.optional ?? null,
      requiredProperties: (module.requiredProperties as PageModule['requiredProperties']) ?? null,
      skipContentParameters: module.skipContentParameters ?? null
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [module, uiModuleType]
  );

  const [showEditModal, setShowEditModal] = useState(false);

  const handleToggle = () => setOpen(!open);

  const handleEditModule = () => {
    if (onEditModule) {
      onEditModule();
    } else {
      setShowEditModal(true);
    }
    setOpen(false);
  };

  const handleSaveModule = (updatedModule: PageModule) => {
    const updatedSlot: FlatSlot = {
      ...module,
      uiModuleFields: updatedModule.uiModuleFields ?? undefined,
      maxItemsOverride: updatedModule.maxItemsOverride ?? undefined,
      minItemsOverride: updatedModule.minItemsOverride ?? undefined,
      ignoreBaseQuery: updatedModule.ignoreBaseQuery ?? undefined,
      optional: updatedModule.optional ?? undefined,
      requiredProperties: (updatedModule.requiredProperties as string[] | null | undefined) ?? undefined,
      skipContentParameters: updatedModule.skipContentParameters ?? undefined
    };
    updateModule(moduleIndex, updatedSlot);
  };

  const handleRemoveModule = () => {
    removeModule(moduleIndex);
    setOpen(false);
  };

  const handleMoveUp = () => {
    if (!isFirst) reorderModules(swapAdjacentItems(modules, moduleIndex, 'up'));
  };

  const handleMoveDown = () => {
    if (!isLast) reorderModules(swapAdjacentItems(modules, moduleIndex, 'down'));
  };

  const treatmentLabel = useMemo(() => {
    if (currentTreatmentType) {
      const setting = findTreatmentSetting(treatmentTypeSettings, currentTreatmentType);
      if (setting?.label) return setting.label;
    }
    return currentTreatmentType;
  }, [currentTreatmentType, treatmentTypeSettings]);

  const currentTreatmentSetting = findTreatmentSetting(treatmentTypeSettings, currentTreatmentType);
  const effectiveMin = minItemsOverride ?? currentTreatmentSetting?.minRequiredContentItems;
  const effectiveMax = maxItemsOverride ?? currentTreatmentSetting?.maxRequiredContentItems;
  const effectiveMessage =
    effectiveMin != null || effectiveMax != null
      ? `${currentTreatmentSetting?.optional ? 'Optional' : 'Must be present'}. ${effectiveMin ?? 1} to ${
          effectiveMax ?? '?'
        } items.`
      : currentTreatmentSetting?.message;

  const moduleFieldKey = getModuleFieldKey(uiModuleType ?? UiModuleType.UiBasicModuleType);
  const subsetTreatmentTypeSettings = getSubsetTreatmentTypeSettings(
    uiModuleType ?? UiModuleType.UiBasicModuleType,
    treatmentTypeSettings
  );
  const swappableTreatmentTypes: string[] =
    (uiModuleFields[moduleFieldKey] as { allowedTreatmentTypes?: string[] } | null)?.allowedTreatmentTypes ??
    (currentTreatmentType ? [currentTreatmentType] : []);
  const allowedTreatmentTypeSettings = subsetTreatmentTypeSettings.filter(
    (s) =>
      swappableTreatmentTypes.includes(s.treatmentType) || swappableTreatmentTypes.includes(s.treatmentTypeKey ?? '')
  );

  const isTreatmentDropdownDisabled = allowedTreatmentTypeSettings.length <= 1;

  return (
    <>
      <Draggable draggableId={`module-${moduleIndex}`} index={localIndex}>
        {(provided) => (
          <div className={styles.draggableModule} ref={provided.innerRef} {...provided.draggableProps}>
            <Wrapper className={styles.moduleWrapper} margin={{ bottom: 'mlg' }}>
              <Group align="space-between" margin={{ bottom: 'xs' }}>
                <Group gap="none" data-testid="page-treatment-header">
                  {isTreatmentDropdownDisabled ? (
                    <Typography
                      variant="note"
                      color="asphalt"
                      margin={{ bottom: 'none' }}
                      data-testid="treatment-type-label-disabled"
                    >
                      {treatmentLabel}
                      {currentTreatmentType ? ` (${currentTreatmentType})` : ''}
                    </Typography>
                  ) : (
                    <Dropdown
                      open={treatmentOpen}
                      onToggle={() => setTreatmentOpen(!treatmentOpen)}
                      componentRef={treatmentComponentRef}
                      data-testid="treatment-type-dropdown"
                    >
                      <Dropdown.Trigger>
                        <Group
                          direction="row"
                          gap="xs"
                          className={classnames('st_focusable', styles.treatmentTypeTrigger)}
                          componentEl="button"
                          data-testid="treatment-type-trigger"
                        >
                          <Typography data-testid="treatment-type-label" color="asphalt" variant="note">
                            {currentTreatmentSetting?.label ?? treatmentLabel}
                            {currentTreatmentType ? ` (${currentTreatmentType})` : ''}
                          </Typography>
                          <IconCaretDown color="asphalt" data-testid="treatment-type-caret" />
                        </Group>
                      </Dropdown.Trigger>
                      <Dropdown.Content padding={{ all: 'sm' }} data-testid="treatment-type-menu">
                        <Group direction="column" gap="xs" data-testid="treatment-type-options">
                          <Typography variant="note" color="asphalt" margin={{ bottom: 'xs' }}>
                            Page editors can swap between these:
                          </Typography>
                          {allowedTreatmentTypeSettings.map((setting, idx) => (
                            <Button
                              data-testid={`treatment-type-option-${idx}`}
                              fullWidth
                              tertiary
                              key={setting.treatmentType}
                              disabled={!onChangeTreatmentType}
                              onClick={
                                onChangeTreatmentType
                                  ? () => {
                                      onChangeTreatmentType(setting.treatmentTypeKey ?? setting.treatmentType);
                                      setTreatmentOpen(false);
                                    }
                                  : undefined
                              }
                            >
                              {setting.label}
                            </Button>
                          ))}
                        </Group>
                      </Dropdown.Content>
                    </Dropdown>
                  )}
                </Group>
                <Dropdown
                  open={open}
                  position="right"
                  trigger={<IconThreeDotsVer color="asphalt" />}
                  onToggle={handleToggle}
                  componentRef={componentRef}
                  data-testid="page-draft-ellipsis-icon"
                  padding={{ all: 'none' }}
                >
                  <Wrapper padding={{ all: 'md' }}>
                    <Button tertiary fullWidth icon={IconGear} onClick={handleEditModule}>
                      Module Options
                    </Button>
                  </Wrapper>
                  <Divider />
                  <Wrapper padding={{ all: 'md' }}>
                    <Button
                      tertiary
                      fullWidth
                      icon={IconMinusCircle}
                      onClick={handleRemoveModule}
                      color="lava"
                      data-testid="remove-module-button"
                    >
                      Remove module
                    </Button>
                  </Wrapper>
                </Dropdown>
              </Group>

              <UiModuleFieldsHeader pageModule={asPageModule} />

              {extraContent ?? <Wrapper padding={{ all: 'xs' }} className={styles.moduleBox} />}
              {effectiveMessage && (
                <Typography variant="note" color="asphalt" margin={{ top: 'xs', bottom: 'none' }}>
                  {effectiveMessage}
                </Typography>
              )}
            </Wrapper>

            <ReorderControls
              isFirst={isFirst}
              isLast={isLast}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              dragHandleProps={provided.dragHandleProps ?? undefined}
              dragHandleTestId={`module-drag-handle-${localIndex}`}
            />
          </div>
        )}
      </Draggable>

      {showEditModal && (
        <EditModuleModal
          module={asPageModule}
          treatmentSetting={
            currentTreatmentSetting
              ? {
                  minRequiredContentItems: currentTreatmentSetting.minRequiredContentItems,
                  maxRequiredContentItems: currentTreatmentSetting.maxRequiredContentItems,
                  contentTypes: currentTreatmentSetting.contentTypes,
                  optional: currentTreatmentSetting.optional,
                  message: currentTreatmentSetting.message,
                  ignoreBaseQuery: currentTreatmentSetting.ignoreBaseQuery,
                  skipContentParameters: currentTreatmentSetting.skipContentParameters,
                  requiredProperties: currentTreatmentSetting.requiredProperties
                }
              : null
          }
          groupAllowedTreatmentTypes={groupAllowedTreatmentTypes}
          catalogTreatmentTypes={subsetTreatmentTypeSettings.map((s) => s.treatmentTypeKey ?? s.treatmentType)}
          onSave={handleSaveModule}
          onDismiss={() => setShowEditModal(false)}
        />
      )}
    </>
  );
};
